import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  useTheme,
  useMediaQuery,
  // Badge,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { motion } from 'framer-motion';
import logo from '../images/logo.png';
import { AuthContext } from '../auth/AuthContext';

const Navbar = () => {
  const { currentUser, userLogout } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const links = [
    { label: 'Home', path: '/' },
    { label: 'Issues', path: '/issues' },
    // { label: 'Reviews', path: '/reviews' },
    { label: 'Contact Us', path: '/contact/form' },
  ];

  if (currentUser) {
    links.push({ label: 'Dashboard', path: '/dashboard' });
  }

  const handleLogout = () => {
    userLogout();
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Report Now button component (reused in desktop and mobile)
  const ReportNowButton = ({ size = 'medium', fullWidth = false }) => (
    <Button
      variant="contained"
      size={size}
      fullWidth={fullWidth}
      onClick={() => {navigate('/report')
        setDrawerOpen(false)
      }}
      sx={{
        background: 'linear-gradient(135deg, #e6c000, #00c853)',
        fontWeight: 600,
        px: size === 'large' ? 4 : 2.5,
        borderRadius: '14px',
        textTransform: 'none',
        color: 'rgb(8, 73, 68)',
        boxShadow: '0px 4px 12px rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.2)',
        '&:hover': {
          background: 'linear-gradient(135deg, #00c853, #009624)',
          transform: 'scale(1.02)',
          boxShadow: '0px 6px 20px rgba(0,200,80,0.4)',
        },
        '&:active': {
          transform: 'scale(0.98)',
        },
        transition: 'all 0.2s ease',
        ...(fullWidth && { 
          justifyContent: 'center',
          py: 1.5,
        }),
      }}
    >
      Report Now
    </Button>
  );

  const drawer = (
    <Box
      sx={{
        width: 280,
        pt: 5,
        backgroundColor: 'rgba(0, 60, 30, 0.95)',
        height: '100%',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <List sx={{ flex: 1 }}>
        {/* Report Now at top of drawer for visibility */}
        <ListItem sx={{ px: 2, mb: 2 }}>
          <ReportNowButton size="large" fullWidth />
        </ListItem>

        {links.map(({ label, path }) => (
          <ListItem
            button
            key={label}
            onClick={() => {
              navigate(path);
              setDrawerOpen(false);
            }}
            sx={{
              '&:hover': { backgroundColor: 'rgba(141, 240, 159, 0.2)' },
              borderRadius: 1,
              mx: 1,
            }}
          >
            <ListItemText primary={label} />
          </ListItem>
        ))}

        {currentUser ? (
          <ListItem
            button
            onClick={() => {
              handleLogout();
              setDrawerOpen(false);
            }}
            sx={{
              '&:hover': { backgroundColor: 'rgba(141, 240, 159, 0.2)' },
              borderRadius: 1,
              mx: 1,
            }}
          >
            <ListItemText primary="Logout" />
          </ListItem>
        ) : (
          <ListItem
            button
            onClick={() => {
              navigate('/login');
              setDrawerOpen(false);
            }}
            sx={{
              '&:hover': { backgroundColor: 'rgba(141, 240, 159, 0.2)' },
              borderRadius: 1,
              mx: 1,
            }}
          >
            <ListItemText primary="Login" />
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={3}
      sx={{
        backgroundColor: 'rgba(141, 240, 159, 0.85)',
        color: '#003c1e',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid rgba(0,60,30,0.08)',
      }}
      component={motion.div}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Toolbar sx={{ py: 0.5 }}>
        {/* Logo + Brand */}
        <Box
          display="flex"
          alignItems="center"
          sx={{ flexGrow: 1, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <img src={logo} alt="Logo" style={{ height: 45, marginRight: 12 }} />
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ 
              color: '#003c1e', 
              letterSpacing: 1,
              display: { xs: 'none', sm: 'block' }
            }}
          >
            MazGuard
          </Typography>
        </Box>

        {/* Links + Buttons */}
        {isMobile ? (
          <>
            <Box display="flex" alignItems="center" gap={1}>
              {/* Show Report Now button on mobile navbar as icon */}
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/report')}
                sx={{
                  background: 'linear-gradient(135deg, #e6c000, #00c853)',
                  fontWeight: 600,
                  borderRadius: '10px',
                  textTransform: 'none',
                  color: 'rgb(8, 73, 68)',
                  boxShadow: '0px 2px 8px rgba(0,0,0,0.2)',
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.8,
                  fontSize: '0.75rem',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #00c853, #009624)',
                  },
                }}
              >
                📢 Report
              </Button>
              <IconButton color="inherit" edge="end" onClick={toggleDrawer}>
                <MenuIcon />
              </IconButton>
            </Box>
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              PaperProps={{
                sx: {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                }
              }}
            >
              {drawer}
            </Drawer>
          </>
        ) : (
          <Box display="flex" gap={2} alignItems="center">
            {links.map(({ label, path }) => (
              <Button
                key={label}
                onClick={() => navigate(path)}
                sx={{
                  color: '#003c1e',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  '&:hover': {
                    color: '#0a7d40',
                    backgroundColor: 'rgba(141, 240, 159, 0.2)',
                  },
                }}
              >
                {label}
              </Button>
            ))}

            {/* Report Now Button - Desktop */}
            <ReportNowButton size="large" />

            {currentUser ? (
              <Button
                onClick={handleLogout}
                variant="outlined"
                sx={{
                  borderRadius: 5,
                  borderColor: '#ff7043',
                  color: '#ff7043',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { 
                    backgroundColor: '#ff7043',
                    color: '#fff',
                    borderColor: '#ff7043',
                  },
                }}
              >
                Logout
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                variant="outlined"
                sx={{
                  borderRadius: 5,
                  borderColor: '#0a7d40',
                  color: '#0a7d40',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { 
                    backgroundColor: '#0a7d40',
                    color: '#fff',
                    borderColor: '#0a7d40',
                  },
                }}
              >
                Login
              </Button>
            )}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;